import { ChangeDetectionStrategy, Component, Injector, inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { BusinessUnitParameterModel, ILightWeightObject } from '@fi-shared/models';
import { BusinessUnitService } from '../business-unit.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { ModuleType } from '@fi-shared/types/module-type';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { takeUntil } from 'rxjs/operators';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { ILoadDataArgs } from '@fi-app/shared/fi-items-list/fi-data-source-base.directive';
import { IItemsListAction2, IItemsListDataFetchResult } from '@fi-app/shared/fi-items-list/fi-items-list';
import { FiImageSize, ObjectTypes } from '@fi-app/shared/types';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { IBusinessUnitModel } from '@fi-app/business-unit/business-unit.model';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';
import { IPreviewImageCellValue } from '@fi-shared/fi-wrapper-table/fi-wrapper-table';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { WrapperTableContactTypes } from '@fi-shared/fi-wrapper-table/cells/fi-wrapper-table-contacts-cell/fi-wrapper-table-contacts-cell.component';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { FiDisplayType } from '@fi-shared/types/display-type';
import { isDefined } from '@fi-shared/utils';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { getSystemObjectColorClass } from '@fi-shared/utils/getSystemObjectColorClass';

@Component({
    selector: 'fi-business-unit-list',
    templateUrl: './business-unit-list.component.html',
    styleUrls: ['./business-unit-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-business-unit-list' },
    standalone: false,
})
export class BusinessUnitListComponent extends FiRemoteStateFullDataSource<IBusinessUnitModel> implements OnInit {
    @Input() isActionsSearchHeaderShown: boolean = true;
    @Input() isCheckboxColumnShown: boolean = true;
    @Input() isAreaColumnShown: boolean = true;
    @Input() businessAreaId: number;
    @Input() areaId: number;
    @Input() clusterId: number;
    @Input() isClusterColumnShown: boolean;
    @Input() listDisplayType: FiDisplayType;

    private businessUnitService = inject(BusinessUnitService);
    private stateAccessCheckService = inject(StateAccessCheckService);
    private statisticService = inject(StatisticService);
    private fiMediaStorageService = inject(FiMediaStorageService);
    private fiItemsDeleteHelperService = inject(FiItemsDeleteHelperService);

    public defaultSortColumnName: string = 'Name';
    public defaultSortDirection: boolean = false;
    public objectType: ObjectTypes = ObjectTypes.BusinessUnit;
    public itemsListType = FiItemsListType.Units;

    protected businessUnitAccessRight: boolean = this.stateAccessCheckService.isStateAllowedForCurrentUser(
        FiStateNames.BusinessUnit.Add,
    );
    protected itemsListActions: IItemsListAction2[] = [
        {
            title: this.appTextService.get('common.delete'),
            onClick: () => this.confirmDelteSelectedItems(),
        },
    ];

    protected readonly FiStateNames = FiStateNames;

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnInit(): void {
        this.setListDataSettings();
        this.subscribeOnBusinessUnitDelete();
        if (!isNullOrUndefined<number>(this.fiStateService.params.BusinessUnitId))
            this.openBusinessUnitProfileDialog(this.fiStateService.params.BusinessUnitId);
    }

    private setListDataSettings() {
        if (isDefined(this.listDisplayType)) {
            this.currentListDisplayType = this.listDisplayType;
        } else {
            this.setCurrentListDisplayType();
            this.subscribeOnListDisplayTypeChange();
        }

        this.statisticService.Visit(ModuleType.Core);
    }

    private confirmDelteSelectedItems(): void {
        this.fiItemsDeleteHelperService.commonDeleteConfirm(ObjectTypes.BusinessUnit, this.selectedItems()).then(() => {
            this.deleteSelectedItems();
        });
    }

    private deleteSelectedItems(): void {
        const selectedItems = this.selectedItems();
        const selectedItemIds: number[] = selectedItems.map((selectedItem: IBusinessUnitModel) => {
            return selectedItem.Id;
        });
        this.businessUnitService.deleteBusinessUnits(selectedItemIds).then(() => {
            this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                ObjectTypes.BusinessUnit,
                selectedItemIds,
                selectedItems[0].Name,
            );
            this.reloadData();
        });
    }

    private subscribeOnBusinessUnitDelete(): void {
        this.businessUnitService.onRemoveBusinessUnitFromList$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.reloadData());
    }

    protected openBusinessUnitProfileDialog(businessUnitId: number, $event?: MouseEvent): void {
        if (isDefined($event)) {
            $event.stopPropagation();
        }
        this.businessUnitService.openBusinessUnitViewDialog(businessUnitId);
    }

    protected getBusinessUnitImageCellValue(businessUnitModel: IBusinessUnitModel): IPreviewImageCellValue {
        return {
            image: this.fiMediaStorageService.getMediaFileUrl(
                {
                    PhysicalFileName: businessUnitModel.MediaBankPhysicalFileName,
                    Extension: businessUnitModel.MediaBankExtension,
                },
                { size: this.isDisplayTypeListGrid() ? FiImageSize.Medium : FiImageSize.Thumbnail },
            ),
            imageLarge: '',
            iconClass: 'fas fa-shop BrandedColor',
        };
    }

    protected getUnitBusinessAreas(businessUnitModel: IBusinessUnitModel): INameIdObj[] {
        return businessUnitModel.BusinessAreas.map((businessArea: ILightWeightObject) => {
            return { Name: businessArea.Name, Id: businessArea.Id };
        });
    }

    protected navigateToBusinessArea(businessAreaId: number): void {
        this.fiStateService.go(FiStateNames.BusinessArea.View, { Id: businessAreaId });
    }

    public loadListDataFn(loadArgs: ILoadDataArgs): IFiHttpPromise<IItemsListDataFetchResult<IBusinessUnitModel>> {
        let parameter: BusinessUnitParameterModel = new BusinessUnitParameterModel();
        parameter = Object.assign(parameter, this.getItemsListParameter(loadArgs));
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        if (this.areaId) {
            parameter.AreaIds = [this.areaId];
        }
        if (this.clusterId) {
            parameter.ClusterIds = [this.clusterId];
        }
        if (this.businessAreaId) {
            parameter.BusinessAreaIds = [this.businessAreaId];
        }

        return this.businessUnitService.getBusinessUnitsListData(parameter);
    }

    protected readonly WrapperTableContactTypes = WrapperTableContactTypes;
    protected readonly getSystemObjectColorClass = getSystemObjectColorClass;
}
